import { useMemo } from 'react'
import { DateTime } from 'luxon'

import { Card, Popup, Text, Alert, CardColors } from '@/ui'
import { formatCurrency } from '@/utils/currencyHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { selectIsCatchUpBookkeepingComplete } from '@/components/CatchUpBookkeepingStatus/catchUpBookkeepingStatus.selector'
import {
  selectLastYearProfit,
  selectProjectedProfit,
} from '@/features/goal-setting/goals.selectors'
import { TypographyStyles } from '@/styles'

export const ProfitTab = () => {
  const catchUpBookkeepingComplete = useReselector(
    selectIsCatchUpBookkeepingComplete
  )

  const currentYear = useMemo(() => DateTime.now().year, [])
  const projectedProfit = useReselector(selectProjectedProfit)
  const lastYearProfit = useReselector(selectLastYearProfit)

  return (
    <>
      <Text variant={TypographyStyles.H1} className="mt-10">
        Profit Goal Help
      </Text>
      <Text variant={TypographyStyles.BODY_MD} className="mt-2">
        Profit is the amount that remains after expenses are paid, or your
        take-home earnings before taxes.
        <br />
        <br />
        Based upon your current performance, we’ve projected your profit for the
        year to give you additional context for your goals.
      </Text>

      <Card color={CardColors.LEVEL_1} className="mt-8">
        <Text variant={TypographyStyles.LABEL_MD}>Your Practice Profit</Text>
        <div className="flex mt-6">
          <div className="w-1/2">
            <Text variant={TypographyStyles.LABEL_SM}>
              Projected {currentYear} Profit
              <Popup
                content={
                  <>
                    <Text variant={TypographyStyles.H3}>Projected Profit</Text>
                    <Text variant={TypographyStyles.BODY_SM} className="mt-3">
                      This is calculated by subtracting your projected expenses
                      from your projected income.
                    </Text>
                  </>
                }
              />
            </Text>

            <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
              {formatCurrency(projectedProfit, {
                hideDecimalsIfZero: true,
              })}
            </Text>
          </div>
          <div className="w-1/2">
            <Text variant={TypographyStyles.LABEL_SM}>
              {currentYear - 1} Profit
            </Text>

            <Text variant={TypographyStyles.DISPLAY_XS} className="mt-2">
              {catchUpBookkeepingComplete
                ? formatCurrency(lastYearProfit, {
                    hideDecimalsIfZero: true,
                  })
                : '$-'}
            </Text>
          </div>
        </div>
        {!catchUpBookkeepingComplete && (
          <Alert variant="warning" className="mt-6">
            We’ll be able to provide you with a projected profit once your
            catch-up bookkeeping is complete.
          </Alert>
        )}
      </Card>
    </>
  )
}
